import type { MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EventFilterView } from '@/features/EventFilterView';
import { usePersonEventsData } from '@/features/EventsListLayout/hooks';
import { EngineService } from '@/services/engine';
import { useDeletedItems, useOpenInNewWindow } from '@/hooks';
import { hasRouterHistory } from '@/stores/navigation-history.store';
import { EVENTS_CONFIG } from '../constants';
import type { EventsPageScenario } from '../types';
import { useEventsPageFilters } from '../useEventsPageFilters';
import { calculateSourceCount } from './calculateSourceCount';

export function usePersonEventsScenario(): EventsPageScenario {
    const { t } = useTranslation([
        I18nNamespace.COMMON,
        I18nNamespace.EVENTS,
        I18nNamespace.SOURCES,
    ]);
    const { sourceId, personId, hasSourceFilter, setSearchParams } =
        useEventsPageFilters();
    const { openEvent } = useOpenInNewWindow();

    const { data: person, isLoading: personLoading } = useSWR(
        personId ? `person-${personId}` : null,
        () => EngineService.getPerson(personId!),
    );

    const eventsResult = usePersonEventsData({ personId: personId! });

    const items = useMemo(
        () =>
            eventsResult.occurrences.map((occurrence) => ({
                event: occurrence.event,
                confidence:
                    occurrence.confidence != null
                        ? occurrence.confidence * 100
                        : undefined,
            })),
        [eventsResult.occurrences],
    );

    const onEventClick = useCallback(
        (clickedEventId: string, event?: MouseEvent) => {
            openEvent(clickedEventId, event, {
                state: {
                    from: 'person-events',
                    feedContext: {
                        type: 'person',
                        personId: personId!,
                    },
                },
            });
        },
        [openEvent, personId],
    );

    const { filteredItems, deletedCount } = useDeletedItems({
        items,
        getItemId: (item) => item.event.id,
        deleteItemsFn: (ids) => EngineService.deleteEvents(ids),
        successMessage: t('actions.deleteSuccess'),
        errorMessage: t('actions.deleteError'),
    });

    const sourceCount = useMemo(
        () => calculateSourceCount(filteredItems, eventsResult, sourceId),
        [
            filteredItems,
            eventsResult.totalItems,
            eventsResult.pageSize,
            sourceId,
        ],
    );

    const handleClearPerson = useCallback(() => {
        setSearchParams(
            (previousSearchParams) => {
                const nextSearchParams = new URLSearchParams(
                    previousSearchParams,
                );
                nextSearchParams.delete('personId');
                return nextSearchParams;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    return {
        pageKey: `person-events-${personId}`,
        items: filteredItems,
        eventCount: eventsResult.totalItems
            ? eventsResult.totalItems - deletedCount
            : filteredItems.length,
        error: eventsResult.error,
        isLoading: eventsResult.isLoading || (personId ? personLoading : false),
        pagination: {
            mode: 'paginated',
            page: eventsResult.page,
            pageSize: eventsResult.pageSize,
            totalCount: eventsResult.totalItems,
            isFetching: eventsResult.isFetching,
            onPageChange: eventsResult.onPageChange,
        },
        isEmpty: filteredItems.length === 0,
        sourceCount,
        emptyMessage: t('events:page.noResults'),
        showBackButton: !!personId || hasSourceFilter || hasRouterHistory(),
        backButtonLabel: t('page.backToPersons'),
        filterView:
            hasSourceFilter || !!personId ? (
                <EventFilterView
                    config={EVENTS_CONFIG}
                    personId={personId}
                    person={person}
                    onClearPerson={personId ? handleClearPerson : undefined}
                />
            ) : undefined,
        onEventClick,
    };
}
